import React, { useState, useMemo } from 'react';
import DataTable from '../ui/DataTable/DataTable';
import { CellText, CellNumber, CellStatus, CellTag } from '../ui/DataTable/cells';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';

// Invygo / Yelo remarks read as a category tag (Invygo purple, Yelo amber); other remarks are plain text.
const remarksCell = (text) => {
    const str = String(text || '');
    const lower = str.toLowerCase();
    if (lower.includes('invygo')) return <CellTag tone="accent">{str}</CellTag>;
    if (lower.includes('yelo')) return <CellTag tone="warning">{str}</CellTag>;
    return <CellText>{str}</CellText>;
};

// Car status from the rental system: "ready" is a good state the shared map doesn't know;
// "rented" stays blue as before.
const CAR_STATUS_TONES = { ready: 'success', rented: 'info' };
const CarStatusCell = ({ status }) => {
    if (!status) return <span className="ds-cell-empty">—</span>;
    return <CellStatus status={status} tone={CAR_STATUS_TONES[String(status).toLowerCase()]} />;
};

// "Peugeot" / "PEUGEOT " are one manufacturer: group by a normalised key, show the most common spelling.
const normKey = (v) => String(v ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
const tidy = (v) => String(v ?? '').trim().replace(/\s+/g, ' ');

// What a car is doing now (usageOf(car) from the Fleet page): its Ejar status says whether it is
// rented, the open contract gives the category. One column per category, then "Not rented".
const CATEGORIES = ['Monthly', 'Daily', 'Invygo', 'Leasing', 'Airport', 'Rented'];
const HEADERS = { Rented: 'Other rental', free: 'Not rented' };
const labelOf = (key) => (key === 'all' ? 'All' : HEADERS[key] || key);

const SummaryView = ({ cars, getCarStatus, usageOf, rentalsLoading = false }) => {
    // State for drilling down into a model
    const [selectedModelSummary, setSelectedModelSummary] = useState(null); // { key, manufacturer, model, filter }

    const bucketOf = (car) => (usageOf ? usageOf(car) : null) || 'free';

    // 1. Calculate Summary Data
    const summaryData = useMemo(() => {
        const groups = new Map();
        cars.forEach((car) => {
            const make = tidy(car.make) || 'Unknown';
            const model = tidy(car.model) || 'Unknown';
            const key = `${normKey(make)}|${normKey(model)}`;
            const g = groups.get(key) || { key, makes: new Map(), models: new Map(), total: 0, free: 0, ...Object.fromEntries(CATEGORIES.map((c) => [c, 0])) };
            g.makes.set(make, (g.makes.get(make) || 0) + 1);
            g.models.set(model, (g.models.get(model) || 0) + 1);
            g.total += 1;
            g[bucketOf(car)] += 1;
            groups.set(key, g);
        });
        const top = (m) => [...m.entries()].sort((a, b) => b[1] - a[1])[0][0];
        return [...groups.values()]
            .map(({ makes, models, ...g }) => ({ ...g, manufacturer: top(makes), model: top(models) }))
            .sort((a, b) => b.total - a.total);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cars, usageOf]);

    // Filter Logic for Drill Down
    const drillDownData = useMemo(() => {
        if (!selectedModelSummary) return [];
        const { key, filter } = selectedModelSummary;
        return cars.filter((car) => {
            if (`${normKey(tidy(car.make) || 'Unknown')}|${normKey(tidy(car.model) || 'Unknown')}` !== key) return false;
            return filter === 'all' || bucketOf(car) === filter;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedModelSummary, cars, usageOf]);

    const countCell = (row, filter) => {
        const value = row[filter === 'all' ? 'total' : filter];
        return (
            <Button
                onClick={() => setSelectedModelSummary({ key: row.key, manufacturer: row.manufacturer, model: row.model, filter })}
                variant="link"
                size="sm"
                disabled={value === 0}
                aria-label={`Show ${labelOf(filter)} ${row.model} cars (${value})`}
            >
                <CellNumber value={rentalsLoading && filter !== 'all' ? '…' : value} />
            </Button>
        );
    };

    // a category column only when at least one car is in it
    const used = CATEGORIES.filter((c) => summaryData.some((r) => r[c] > 0));
    const summaryColumns = [
        { key: 'manufacturer', header: 'Manufacturer', type: 'text' },
        { key: 'model', header: 'Model', type: 'text' },
        { key: 'total', header: 'Total', cell: (row) => countCell(row, 'all') },
        ...used.map((c) => ({ key: c, header: labelOf(c), cell: (row) => countCell(row, c) })),
        { key: 'free', header: 'Not rented', cell: (row) => countCell(row, 'free') },
    ];

    const drillColumns = [
        { key: 'plate_number', header: 'Plate No', type: 'plate' },
        { key: 'category', header: 'Class', type: 'text' },
        { key: 'status', header: 'Status', type: 'status' },
        {
            key: 'car_status',
            header: 'Car Status',
            accessor: (row) => (getCarStatus ? getCarStatus(row.plate_number) : ''),
            cell: (row, value) => <CarStatusCell status={value} />,
        },
        { key: 'registration_expiry', header: 'Reg Exp', nowrap: true, type: 'date' },
        { key: 'color', header: 'Color', type: 'text' },
        { key: 'remarks', header: 'Remarks', cell: (row) => remarksCell(row.remarks) },
    ];

    if (!selectedModelSummary) {
        return (
            <div className="w-full mt-6">
                <DataTable
                    title={<span className="inline-flex items-center gap-2"><Icon glyph={ICONS.reports} size="sm" tone="muted" /> Summary by Model ({summaryData.length})</span>}
                    columns={summaryColumns}
                    data={summaryData}
                    rowKey={(row) => row.key}
                    searchPlaceholder="Search manufacturer or model…"
                    pagination={false}
                    emptyTitle="No cars to summarise"
                />
            </div>
        );
    }

    const filterLabel = labelOf(selectedModelSummary.filter);

    return (
        <div className="w-full mt-6 flex flex-col gap-3">
            <div className="ds-toolbar">
                <Button
                    onClick={() => setSelectedModelSummary(null)}
                    size="sm"
                    leadingIcon={<Icon glyph={ICONS.back} size="sm" />}
                >
                    Back to Summary
                </Button>
            </div>
            <DataTable
                title={(
                    <span>
                        {selectedModelSummary.model}
                        <span className="text-gray-500"> / </span>
                        <span>{filterLabel}</span>
                        <span className="text-gray-500 font-normal"> ({drillDownData.length})</span>
                    </span>
                )}
                columns={drillColumns}
                data={drillDownData}
                rowKey={(row, i) => `${row.plate_number}-${i}`}
                searchPlaceholder="Search plate, colour, remarks…"
                emptyTitle="No cars in this group"
            />
        </div>
    );
};

export default SummaryView;
